import { useState, FormEvent } from 'react';
import { motion } from 'framer-motion';

export default function FreiburgContact() {
  const [status, setStatus] = useState<'idle' | 'sending' | 'success' | 'error'>('idle');

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const data = new FormData(form);

    // Textarea-Zeichenlimit prüfen
    const message = data.get('message') as string;
    if (message && message.length > 500) {
      setStatus('error');
      return;
    }

    setStatus('sending');
    try {
      const body = new URLSearchParams();
      data.forEach((value, key) => body.append(key, value as string));

      const res = await fetch('https://readdy.ai/api/form/d6lje02qoe30lj0v8180', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
      });

      if (res.ok) {
        setStatus('success');
        form.reset();
      } else {
        setStatus('error');
      }
    } catch {
      setStatus('error');
    }
  };

  return (
    <section id="kontakt" className="py-20 sm:py-28 bg-brand-beige-light">
      <div className="max-w-6xl mx-auto px-4 sm:px-6">
        <div className="grid lg:grid-cols-2 gap-12 items-start">
          {/* Left Info */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <span className="inline-block bg-brand-green/10 text-brand-green text-xs font-semibold px-4 py-1.5 rounded-full mb-4 uppercase tracking-wider">
              Kontakt
            </span>
            <h2 className="text-4xl md:text-5xl font-caprasimo text-brand-black mb-5 leading-tight">
              Kostenloses Erstgespräch für Freiburg
            </h2>
            <p className="text-brand-grey text-base leading-relaxed mb-8">
              Sie suchen professionelles <strong className="text-brand-black">Webdesign in Freiburg</strong>? Ich berate Sie kostenlos und unverbindlich – und erstelle Ihnen ein individuelles Angebot innerhalb von 24 Stunden.
            </p>

            <div className="space-y-5 mb-8">
              {[
                { icon: 'ri-phone-line', label: 'Telefon', value: '0162 365 1571', href: 'tel:01623651571' },
                { icon: 'ri-mail-line', label: 'E-Mail', value: 'info-aiagents@web.de', href: 'mailto:info-aiagents@web.de' },
                { icon: 'ri-map-pin-2-line', label: 'Standort', value: 'Titisee-Neustadt (nahe Freiburg)', href: undefined },
                { icon: 'ri-time-line', label: 'Antwortzeit', value: 'Innerhalb von 24 Stunden', href: undefined },
              ].map(({ icon, label, value, href }) => (
                <div key={label} className="flex items-center gap-4">
                  <div className="w-11 h-11 flex items-center justify-center bg-white rounded-xl shadow-sm flex-shrink-0">
                    <i className={`${icon} text-xl text-brand-green`} />
                  </div>
                  <div>
                    <p className="text-xs text-brand-grey uppercase tracking-wider">{label}</p>
                    {href ? (
                      <a href={href} className="text-brand-black font-medium text-sm hover:text-brand-green transition-colors cursor-pointer">
                        {value}
                      </a>
                    ) : (
                      <p className="text-brand-black font-medium text-sm">{value}</p>
                    )}
                  </div>
                </div>
              ))}
            </div>

            {/* Trust badges */}
            <div className="flex flex-wrap gap-3">
              {['Kostenlos & unverbindlich', 'DSGVO-konform', 'Antwort in 24h'].map((badge) => (
                <span key={badge} className="inline-flex items-center gap-1.5 bg-white border border-brand-beige text-brand-grey text-xs font-medium px-3 py-1.5 rounded-full">
                  <i className="ri-shield-check-line text-brand-green text-sm" />
                  {badge}
                </span>
              ))}
            </div>
          </motion.div>

          {/* Form */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            viewport={{ once: true }}
            className="bg-white rounded-2xl p-8 shadow-xl border border-brand-beige"
          >
            {status === 'success' ? (
              <div className="text-center py-12">
                <div className="w-16 h-16 flex items-center justify-center bg-brand-green/10 rounded-full mx-auto mb-4">
                  <i className="ri-check-line text-3xl text-brand-green" />
                </div>
                <h3 className="text-xl font-caprasimo text-brand-black mb-2">Nachricht gesendet!</h3>
                <p className="text-brand-grey text-sm">Ich melde mich innerhalb von 24 Stunden bei Ihnen. Danke für Ihr Interesse!</p>
                <button
                  onClick={() => setStatus('idle')}
                  className="mt-6 text-brand-green hover:text-brand-green-light font-medium text-sm transition-colors cursor-pointer"
                >
                  Weitere Anfrage senden
                </button>
              </div>
            ) : (
              <form
                data-readdy-form
                id="freiburg-contact-form"
                onSubmit={handleSubmit}
                className="space-y-5"
              >
                <h3 className="text-xl font-caprasimo text-brand-black mb-6">Jetzt anfragen</h3>

                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-semibold text-brand-black mb-1.5 uppercase tracking-wider">
                      Vorname *
                    </label>
                    <input
                      type="text"
                      name="vorname"
                      required
                      placeholder="Max"
                      className="w-full border border-brand-beige rounded-lg px-4 py-3 text-sm text-brand-black placeholder-brand-grey focus:outline-none focus:border-brand-green transition-colors bg-brand-beige-light/40"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-brand-black mb-1.5 uppercase tracking-wider">
                      Nachname *
                    </label>
                    <input
                      type="text"
                      name="nachname"
                      required
                      placeholder="Mustermann"
                      className="w-full border border-brand-beige rounded-lg px-4 py-3 text-sm text-brand-black placeholder-brand-grey focus:outline-none focus:border-brand-green transition-colors bg-brand-beige-light/40"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-semibold text-brand-black mb-1.5 uppercase tracking-wider">
                    E-Mail *
                  </label>
                  <input
                    type="email"
                    name="email"
                    required
                    placeholder="max@beispiel.de"
                    className="w-full border border-brand-beige rounded-lg px-4 py-3 text-sm text-brand-black placeholder-brand-grey focus:outline-none focus:border-brand-green transition-colors bg-brand-beige-light/40"
                  />
                </div>

                <div>
                  <label className="block text-xs font-semibold text-brand-black mb-1.5 uppercase tracking-wider">
                    Telefon
                  </label>
                  <input
                    type="tel"
                    name="telefon"
                    placeholder="0761 123456"
                    className="w-full border border-brand-beige rounded-lg px-4 py-3 text-sm text-brand-black placeholder-brand-grey focus:outline-none focus:border-brand-green transition-colors bg-brand-beige-light/40"
                  />
                </div>

                <div>
                  <label className="block text-xs font-semibold text-brand-black mb-1.5 uppercase tracking-wider">
                    Leistung
                  </label>
                  <select
                    name="leistung"
                    className="w-full border border-brand-beige rounded-lg px-4 py-3 text-sm text-brand-black focus:outline-none focus:border-brand-green transition-colors bg-brand-beige-light/40 cursor-pointer"
                  >
                    <option value="">Bitte wählen…</option>
                    <option value="Neue Webseite">Neue Webseite</option>
                    <option value="Redesign">Redesign</option>
                    <option value="AI Chatbot">AI Chatbot</option>
                    <option value="SEO Optimierung">SEO Optimierung</option>
                    <option value="Sonstiges">Sonstiges</option>
                  </select>
                </div>

                <div>
                  <label className="block text-xs font-semibold text-brand-black mb-1.5 uppercase tracking-wider">
                    Nachricht (max. 500 Zeichen)
                  </label>
                  <textarea
                    name="message"
                    rows={4}
                    maxLength={500}
                    placeholder="Beschreiben Sie kurz Ihr Projekt oder Ihre Frage…"
                    className="w-full border border-brand-beige rounded-lg px-4 py-3 text-sm text-brand-black placeholder-brand-grey focus:outline-none focus:border-brand-green transition-colors bg-brand-beige-light/40 resize-none"
                  />
                </div>

                {status === 'error' && (
                  <p className="text-red-500 text-sm flex items-center gap-2">
                    <i className="ri-error-warning-line" />
                    Fehler beim Senden. Bitte versuchen Sie es erneut.
                  </p>
                )}

                <button
                  type="submit"
                  disabled={status === 'sending'}
                  className="w-full bg-brand-green hover:bg-brand-green-light text-white py-4 rounded-lg font-semibold text-base transition-all shadow-lg hover:shadow-xl hover:scale-105 disabled:opacity-60 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer flex items-center justify-center gap-2"
                >
                  {status === 'sending' ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      Wird gesendet…
                    </>
                  ) : (
                    <>
                      <i className="ri-send-plane-line text-lg" />
                      Kostenlos anfragen
                    </>
                  )}
                </button>

                <p className="text-xs text-brand-grey text-center">
                  Kostenlos &amp; unverbindlich · Antwort innerhalb von 24h
                </p>
              </form>
            )}
          </motion.div>
        </div>
      </div>
    </section>
  );
}
